import apiClient from '../api/apiClient'
import type {
    PotentialManager,
    GroupedManagerResponse,
    PotentialManagersParams,
} from '../types/orgHierarchy'

export const managerSuggestionService = {
    /**
     * Get potential managers for an employee based on org hierarchy
     * Calls /employees/potential-managers which returns prioritized groups:
     * - Priority 1: Same Unit (unit heads first)
     * - Priority 2: Parent Unit
     * - Priority 3: Ancestor Units
     * - Priority 4: Other Employees (all other active employees)
     *
     * If no scope_instance_id is provided, returns all employees as "All Employees"
     */
    async getPotentialManagers(
        params: PotentialManagersParams
    ): Promise<PotentialManager[] | GroupedManagerResponse> {
        const scopeInstanceId = params.scope_instance_id?.trim()

        // Build query params - only include scope_instance_id if provided
        const queryParams: Record<string, any> = {
            grouped: params.grouped ?? true,
        }

        if (scopeInstanceId) {
            queryParams.scope_instance_id = scopeInstanceId
        }

        if (params.employee_id) {
            queryParams.employee_id = params.employee_id
        }

        const { data } = await apiClient.get<GroupedManagerResponse>(
            '/employees/potential-managers',
            { params: queryParams }
        )

        // If grouped response requested, return as-is from backend
        if (params.grouped) {
            return data
        }

        // Flatten groups into single array if not grouped
        const allManagers = data.groups.flatMap(group => group.managers)
        return params.limit ? allManagers.slice(0, params.limit) : allManagers
    },

    /**
     * Get the top suggested manager for an employee
     * Uses the /employees/potential-managers endpoint and returns the suggested field
     */
    async getSuggestedManager(
        scopeInstanceId?: string,
        employeeId?: string
    ): Promise<{ suggested: PotentialManager | null }> {
        const queryParams: Record<string, any> = {
            grouped: true,
        }

        if (scopeInstanceId?.trim()) {
            queryParams.scope_instance_id = scopeInstanceId.trim()
        }

        if (employeeId) {
            queryParams.employee_id = employeeId
        }

        const { data } = await apiClient.get<GroupedManagerResponse>(
            '/employees/potential-managers',
            { params: queryParams }
        )

        return { suggested: data.suggested }
    },
}

export type {
    PotentialManager,
    GroupedManagerResponse,
    PotentialManagersParams,
} from '../types/orgHierarchy'
